import { convertMeasure, isMeasure, isMeasureWithValue } from '@e2m-customer-portal/shared';
import { format, Locale } from 'date-fns';
import { de, enGB } from 'date-fns/locale';
import i18next, { InitOptions, Resource, use } from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationDE from './assets/locales/de/translation.json';
import validationDE from './assets/locales/de/validation.json';
import translationEN from './assets/locales/en/translation.json';
import validationEN from './assets/locales/en/validation.json';

// Fix at https://github.com/i18next/react-i18next/issues/1386 does not support custom separator
type WithOrWithoutPlural<K> = K extends `${infer B}+${'zero' | 'one' | 'two' | 'few' | 'many' | 'other'}` ? B | K : K;

type Translation = typeof translationDE;
type TranslationNormal = Record<WithOrWithoutPlural<keyof Translation>, string>;

type Validation = typeof validationDE;
type ValidationNormal = Record<WithOrWithoutPlural<keyof Validation>, string>;

declare module 'react-i18next' {
  export interface Resources {
    translation: TranslationNormal;
    validation: ValidationNormal;
  }
}

export const resources: Resource = {
  'en-GB': {
    translation: translationEN,
    validation: validationEN,
    nameKey: 'LANGUAGE_ENGLISH',
    locale: enGB,
  },
  'de-DE': {
    translation: translationDE,
    validation: validationDE,
    nameKey: 'LANGUAGE_GERMAN',
    locale: de,
  },
};

export const options: InitOptions = {
  resources,
  lng: 'de-DE',

  keySeparator: false, // we do not use keys in form messages.welcome
  pluralSeparator: '+',
  contextSeparator: '+',

  interpolation: {
    escapeValue: false, // react already safe from xss
    alwaysFormat: true,

    format: (value, fmt, lng, opts) => {
      if (value instanceof Date) {
        if (!fmt) {
          return value.toISOString();
        }

        const lc = i18next.getResource(lng ?? i18next.language, 'locale', '') as Locale;

        return format(value, fmt, { locale: lc });
      }

      if (isMeasure(value)) {
        if (isMeasureWithValue(value)) {
          let measure = value;
          if (opts?.unit != null) {
            measure = convertMeasure(value, opts.unit as string);
          }
          return `${measure.value.toLocaleString(lng ?? i18next.language)}\u202f${measure.unit}`;
        }
        return i18next.getResource(lng ?? i18next.language, 'translation', 'SHARED_UNKNOWN') as string;
      }

      return value as string;
    },
  },
};

// eslint-disable-next-line react-hooks/rules-of-hooks
use(initReactI18next) // passes i18n down to react-i18next
  .init(options)
  .catch((err) => console.error(err));
